import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';

export function errorInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const toastService = inject(ToastService);
  const translate = inject(TranslateService);
  const ignoredStatuses = request.context.get(IGNORED_STATUSES);
  const unexpectedStatuses = [
    HttpStatusCode.Unauthorized,
    HttpStatusCode.Forbidden,
    HttpStatusCode.InternalServerError,
  ];

  return next(request).pipe(
    tap({
      error: (err: any) => {
        if (unexpectedStatuses.includes(err.status)) {
          const path = (err.url ?? 'unknown')
            .replace(/^https?:\/\//, '')
            .split('?')[0];
          const apiMessage =
            err.error?.detail ?? err.error?.Detail ?? err.error?.title ?? '';

          Sentry.captureException(
            `[API error] [Status: ${err.status}] ${path} ${apiMessage}`.trim(),
            {
              // One issue per status + endpoint; message stays searchable as a tag.
              fingerprint: [String(err.status), path],
              tags: { 'api.message': apiMessage },
              extra: { url: err.url, body: err.error },
            }
          );
        }
        if (
          err instanceof HttpErrorResponse &&
          !ignoredStatuses.includes(err.status)
        ) {
          if (err.status === 400 && err.error?.detail) {
            toastService.danger(
              err.error.detail,
              translate.instant('toastr.error'),
              0
            );
          } else if (err.status === 400 && err.error?.Detail) {
            toastService.danger(
              err.error.Detail,
              translate.instant('toastr.error'),
              0
            );
          } else if (
            err.status === 409 &&
            (err.error?.title || err.error?.Detail)
          ) {
            toastService.danger(
              err.error?.title || err.error?.Detail,
              translate.instant('toastr.error'),
              0
            );
          } else if (err.status === 503 && err.error?.detail) {
            // Exporters 503 handle - max 2 concurrent exports limit.
            toastService.danger(err.error.detail, err.error.title, 0);
          }
        }
      },
    })
  );
}

export const IGNORED_STATUSES = new HttpContextToken<number[]>(() => []);
